import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { ScrollManager } from '../utils/scrollManager';
import ProjectForm from '../components/ProjectForm';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { getStatusColors } from '../utils/colorUtils';

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'completed' | 'on-hold' | 'archived';
  created_at: string;
  updated_at: string;
  owner: string;
  tags: string[];
  metadata: Record<string, any>;
}

interface Problem {
  id: string;
  title: string;
  description: string;
  priority: number;
  project_id: string;
}

interface Hypothesis {
  id: string;
  title: string;
  hypothesis_statement: string;
  priority_score: number;
  project_id: string;
}

const ProjectsPage: React.FC = () => {
  const { get, post, put, delete: deleteProject } = useApi();
  const { canWrite, isViewer } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectProblems, setProjectProblems] = useState<Problem[]>([]);
  const [projectHypotheses, setProjectHypotheses] = useState<Hypothesis[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Get current view from URL params
  const currentView = searchParams.get('view') || 'list';
  const projectId = searchParams.get('id');
  const action = searchParams.get('action');
  const returnToReport = searchParams.get('returnToReport') === 'true';

  // Fetch projects and related data from API
  useEffect(() => {
    const fetchProjectsAndRelatedData = async () => {
      try {
        setIsLoading(true);
        const [projectsData, problemsData, hypothesesData] = await Promise.all([
          get<Project[]>('/projects'),
          get<Problem[]>('/problems'),
          get<Hypothesis[]>('/hypotheses')
        ]);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setProjectProblems(Array.isArray(problemsData) ? problemsData : []);
        setProjectHypotheses(Array.isArray(hypothesesData) ? hypothesesData : []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectsAndRelatedData();
  }, [get]);

  // Fetch project details when viewing a specific project
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (projectId && currentView === 'detail') {
        try {
          const [project, problems, hypotheses] = await Promise.all([
            get<Project>(`/projects/${projectId}`),
            get<Problem[]>(`/problems?project_id=${projectId}`),
            get<Hypothesis[]>(`/hypotheses?project_id=${projectId}`)
          ]);
          setSelectedProject(project);
          setProjectProblems(Array.isArray(problems) ? problems : []);
          setProjectHypotheses(Array.isArray(hypotheses) ? hypotheses : []);
        } catch (err) {
          console.error('Error fetching project details:', err);
          setError('Failed to load project details.');
        }
      }
    };

    fetchProjectDetails();
  }, [projectId, currentView, get]);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleProjectCreated = (data: { project: Project }) => {
      setProjects(prev => [...prev, data.project]);
    };

    const handleProjectUpdated = (data: { project: Project }) => {
      setProjects(prev => 
        prev.map(p => p.id === data.project.id ? data.project : p)
      );
      if (selectedProject?.id === data.project.id) {
        setSelectedProject(data.project);
      }
    };

    const handleProjectDeleted = (data: { project_id: string }) => {
      setProjects(prev => prev.filter(p => p.id !== data.project_id));
    };

    const handleProblemCreated = (data: { problem: Problem }) => {
      console.log('Problem created, updating project problems');
      setProjectProblems(prev => [...prev, data.problem]);
    };

    const handleProblemUpdated = (data: { problem: Problem }) => {
      console.log('Problem updated, updating project problems');
      setProjectProblems(prev => 
        prev.map(p => p.id === data.problem.id ? data.problem : p)
      );
    };

    const handleProblemDeleted = (data: { problem_id: string }) => {
      console.log('Problem deleted, updating project problems');
      setProjectProblems(prev => prev.filter(p => p.id !== data.problem_id));
    };

    const handleHypothesisCreated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis created, updating project hypotheses');
      setProjectHypotheses(prev => [...prev, data.hypothesis]);
    };

    const handleHypothesisUpdated = (data: { hypothesis: Hypothesis }) => {
      console.log('Hypothesis updated, updating project hypotheses');
      setProjectHypotheses(prev => 
        prev.map(h => h.id === data.hypothesis.id ? data.hypothesis : h)
      );
    };

    const handleHypothesisDeleted = (data: { hypothesis_id: string }) => {
      console.log('Hypothesis deleted, updating project hypotheses');
      setProjectHypotheses(prev => prev.filter(h => h.id !== data.hypothesis_id));
    };

    socket.on('project_created', handleProjectCreated);
    socket.on('project_updated', handleProjectUpdated);
    socket.on('project_deleted', handleProjectDeleted);
    socket.on('problem_created', handleProblemCreated);
    socket.on('problem_updated', handleProblemUpdated);
    socket.on('problem_deleted', handleProblemDeleted);
    socket.on('hypothesis_created', handleHypothesisCreated);
    socket.on('hypothesis_updated', handleHypothesisUpdated);
    socket.on('hypothesis_deleted', handleHypothesisDeleted);

    return () => {
      socket.off('project_created', handleProjectCreated);
      socket.off('project_updated', handleProjectUpdated);
      socket.off('project_deleted', handleProjectDeleted);
      socket.off('problem_created', handleProblemCreated);
      socket.off('problem_updated', handleProblemUpdated);
      socket.off('problem_deleted', handleProblemDeleted);
      socket.off('hypothesis_created', handleHypothesisCreated);
      socket.off('hypothesis_updated', handleHypothesisUpdated);
      socket.off('hypothesis_deleted', handleHypothesisDeleted);
    };
  }, [socket, isConnected, selectedProject]);

  const handleCreateProject = async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      await post<Project>('/projects', projectData);
      setSearchParams({ view: 'list' });
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Failed to create project. Please try again.');
    }
  };

  const handleUpdateProject = async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    if (!selectedProject) return;
    
    try {
      await put<Project>(`/projects/${selectedProject.id}`, projectData);
      
      // Check if we need to return to report
      if (returnToReport && ScrollManager.hasPendingReturn()) {
        navigate('/reports');
      } else {
        setSearchParams({ view: 'detail', id: selectedProject.id });
      }
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project. Please try again.');
    }
  };

  const confirmDelete = async () => {
    if (!selectedProject) return;
    
    try {
      await deleteProject(`/projects/${selectedProject.id}`);
      setShowDeleteModal(false);
      setSelectedProject(null);
      setSearchParams({ view: 'list' });
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project. Please try again.');
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p className="font-medium">Error loading projects</p>
        <p className="mt-1 text-sm">{error}</p>
      </div>
    );
  }

  // Create Project View
  if (action === 'create') {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setSearchParams({ view: 'list' })}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Projects
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create New Project</h1>
        </div>
        <ProjectForm
          onSubmit={handleCreateProject}
          onCancel={() => setSearchParams({ view: 'list' })}
        />
      </div>
    );
  }

  // Edit Project View
  if (action === 'edit' && selectedProject) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setSearchParams({ view: 'detail', id: selectedProject.id })}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Project Details
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Project: {selectedProject.name}</h1>
        </div>
        <ProjectForm
          project={selectedProject}
          onSubmit={handleUpdateProject}
          onCancel={() => setSearchParams({ view: 'detail', id: selectedProject.id })}
          isEditing
        />
      </div>
    );
  }

  // Project Detail View
  if (currentView === 'detail' && selectedProject) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setSearchParams({ view: 'list' })}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Projects
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedProject.name}</h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(selectedProject.status).background} ${getStatusColors(selectedProject.status).text}`}>
                  {selectedProject.status}
                </span>
                {selectedProject.owner && (
                  <span className="text-sm text-gray-500">Owner: {selectedProject.owner}</span>
                )}
                {selectedProject.tags.length > 0 && (
                  <div className="flex space-x-1">
                    {selectedProject.tags.map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {canWrite() && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setSearchParams({ view: 'detail', id: selectedProject.id, action: 'edit' })}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
                {/* KNOWN ISSUE: Delete confirmation modal has timing delay
                    Despite using separate projectToDelete state (same pattern as working hypothesis delete),
                    the modal still appears with a delay after navigation. Root cause unknown.
                    Functionality works correctly, just with UX timing issue. */}
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
            {isViewer() && (
              <span className="inline-flex items-center px-3 py-2 border border-yellow-200 bg-yellow-50 rounded-md text-sm text-yellow-800">
                <EyeIcon className="h-4 w-4 mr-2" />
                Read-Only Access
              </span>
            )}
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="bg-white shadow-sm rounded-lg p-3 mb-6 border border-gray-200">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <div className="flex items-center">
                  <button
                    onClick={() => setSearchParams({ view: 'list' })}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Projects
                  </button>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-sm font-medium text-gray-900" aria-current="page">
                    {selectedProject.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Description</h2>
          <MarkdownRenderer content={selectedProject.description} />
        </div>

        {/* Project Workspace Section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            🎯 Project Workspace
            <span className="ml-3 text-sm font-normal text-gray-500">
              Manage problems and hypotheses for this project
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Business Problems */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">
                  {projectProblems.length}
                </span>
                Business Problems
              </h3>
              {canWrite() && (
                <button
                  onClick={() => navigate(`/app-problems/new?project_id=${selectedProject.id}`)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Problem
                </button>
              )}
            </div>
            {projectProblems.length > 0 ? (
              <div className="space-y-3">
                {projectProblems.map((problem) => (
                  <div key={problem.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">
                          {problem.title} (Priority: {problem.priority}/10)
                        </h3>
                        <MarkdownRenderer 
                          content={problem.description.length > 150 ? `${problem.description.substring(0, 150)}...` : problem.description}
                          className="mt-1 text-sm text-gray-600"
                        />
                      </div>
                      <button
                        onClick={() => navigate(`/app-problems/${problem.id}`)}
                        className="ml-4 text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No business problems defined for this project yet.</p>
            )}
          </div>

          {/* AI Hypotheses */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">
                  {projectHypotheses.length}
                </span>
                AI Hypotheses
              </h3>
              {canWrite() && (
                <button
                  onClick={() => navigate(`/app-hypotheses/new?project_id=${selectedProject.id}`)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Hypothesis
                </button>
              )}
            </div>
            {projectHypotheses.length > 0 ? (
              <div className="space-y-3">
                {projectHypotheses.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0)).map((hypothesis) => (
                  <div key={hypothesis.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">
                          {hypothesis.title} (Score: {hypothesis.priority_score?.toFixed(2) || 'Not calculated'})
                        </h3>
                        <MarkdownRenderer 
                          content={hypothesis.hypothesis_statement}
                          className="mt-1 text-sm text-gray-600"
                        />
                      </div>
                      <button
                        onClick={() => navigate(`/app-hypotheses/${hypothesis.id}`)}
                        className="ml-4 text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No AI hypotheses defined for this project yet.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Projects List View (default)
  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              {isConnected ? 'Live' : 'Offline'}
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Create and manage projects to organize your business problems and AI hypotheses
          </p>
        </div>
        {canWrite() && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setSearchParams({ action: 'create' })}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Create New Project
            </button>
          </div>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto max-w-md">
            <p className="text-gray-500 mb-4">
              {isViewer()
                ? 'No projects in this organization yet.'
                : 'No projects defined yet. Create one to get started!'}
            </p>
            {canWrite() && (
              <button
                onClick={() => setSearchParams({ action: 'create' })}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Demo Project
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setSearchParams({ view: 'detail', id: project.id })}
                      className="text-left w-full"
                    >
                      <p className="text-lg font-medium text-indigo-600 truncate hover:text-indigo-900">
                        {project.name}
                      </p>
                    </button>
                    <div className="mt-2 flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(project.status).background} ${getStatusColors(project.status).text}`}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-700">
                  {project.description.length > 150 
                    ? `${project.description.substring(0, 150)}...` 
                    : project.description}
                </p>
                
                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <div className="flex space-x-4">
                    <span>Problems: {projectProblems.filter(p => p.project_id === project.id).length}</span>
                    <span>Hypotheses: {projectHypotheses.filter(h => h.project_id === project.id).length}</span>
                  </div>
                </div>
                
                {project.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {project.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                        {tag}
                      </span>
                    ))}
                    {project.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{project.tags.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProject && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Project
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the project "{selectedProject.name}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;